import stylesheet from './TextCardCarousel.scss?inline';

import CustomComponent from "../../../base/customComponent/CustomComponent";
import Glide, {
    Anchors,
    Breakpoints,
    Controls,
    Images,
    Keyboard,
    Swipe
} from "../../../../../node_modules/@glidejs/glide/dist/glide.modular.esm";
import '../../Carousel/CarouselPagination';
import { Breakpoint, getCurrentBreakpoint } from "../../../util/breakpoints";

const GAP_PX = 4; // spacing-1

// Cards shown per view for each breakpoint.
const PER_VIEW_BY_BREAKPOINT: Record<Breakpoint, number> = {
    sm: 1,
    md: 2,
    lg: 3,
    xl: 4,
    xxl: 4,
};

class NXTTextCardCarousel extends CustomComponent {
    glide: Glide;
    private resizeObserver: ResizeObserver;
    private root: HTMLElement;
    private slidesContainer: HTMLElement;
    private pagination: HTMLElement;
    private cardCount = 0;

    constructor() {
        super();
        this.customStyle = stylesheet;
    }

    render() {
        const container = document.createElement('div');
        container.classList.add('text-card-carousel', 'glide');
        container.innerHTML = `
            <div class="carousel-cards">
                <div class="glide__track" data-glide-el="track">
                    <ul class="glide__slides"></ul>
                </div>
            </div>
        `;
        this.root = container;
        this.slidesContainer = container.querySelector('.glide__slides');
        this.cloneCardsIntoSlides(this.slidesContainer);

        if (document.readyState === 'complete') {
            requestAnimationFrame(() => this.setup());
        } else {
            window.addEventListener('load', () => this.setup(), { once: true });
        }
        return container;
    }

    cloneCardsIntoSlides(containerElement: HTMLElement) {
        const slotElements = this.getSlotElements('');
        this.cardCount = slotElements.length;
        slotElements.forEach(content => {
            content.removeAttribute('slot');
            const wrapper = document.createElement('li');
            wrapper.classList.add('glide__slide');
            wrapper.appendChild(content);
            containerElement.appendChild(wrapper);
        });
    }

    private get contentWidth(): number {
        return this.offsetWidth || this.root.offsetWidth;
    }

    private computePerView(): number {
        return Math.min(this.cardCount, PER_VIEW_BY_BREAKPOINT[getCurrentBreakpoint()]);
    }

    // Distance from the page edge to the content edge, so the full-bleed track
    // still starts the first card at the usual content padding.
    private computePeek(): number {
        return Math.max(0, (window.innerWidth - this.contentWidth) / 2);
    }

    private setup() {
        this.mountGlide(this.computePerView());
        this.injectPagination();
        this.observeResize();
    }

    private mountGlide(perView: number) {
        const peek = this.computePeek();
        this.glide = new Glide(this.root, {
            type: 'slider',
            rewind: false,
            bound: true,
            gap: GAP_PX,
            perView,
            peek: { before: peek, after: peek },
        }).mount({ Anchors, Breakpoints, Controls, Images, Keyboard, Swipe });

        this.glide.on(['mount.after', 'run', 'update'], () => this.updatePagination());
    }

    private injectPagination() {
        this.pagination = document.createElement('nxt-carousel-pagination');
        this.pagination.classList.add('text-card-carousel-pagination');
        this.pagination.setAttribute('size', 'lg');
        this.pagination.setAttribute('size-lg', 'md');
        this.pagination.setAttribute('counter-hide-md', '');
        this.root.appendChild(this.pagination);

        this.root.addEventListener('carousel-prev', () => this.glide?.go('<'));
        this.root.addEventListener('carousel-next', () => this.glide?.go('>'));
        this.updatePagination();
    }

    private updatePagination() {
        if (!this.pagination || !this.glide) return;
        const perView = this.glide.settings.perView as number;
        const lastIndex = Math.max(0, this.cardCount - perView);
        const index = this.glide.index;
        this.pagination.toggleAttribute('hidden', lastIndex === 0);
        this.pagination.textContent = `${index + 1}/${this.cardCount}`;
        this.pagination.toggleAttribute('has-prev', index > 0);
        this.pagination.toggleAttribute('has-next', index < lastIndex);
    }

    private observeResize() {
        if (this.resizeObserver) return;
        this.resizeObserver = new ResizeObserver(() => this.handleResize());
        this.resizeObserver.observe(this);
    }

    private handleResize() {
        if (!this.glide) return;
        const peek = this.computePeek();
        this.glide.update({ perView: this.computePerView(), peek: { before: peek, after: peek } });
    }

    private teardownGlide() {
        this.glide?.destroy();
        this.glide = null;
        this.pagination?.remove();
        this.pagination = null;
    }

    // noinspection JSUnusedGlobalSymbols
    disconnectedCallback() {
        this.teardownGlide();
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
    }
}

customElements.define('nxt-text-card-carousel', NXTTextCardCarousel);
